/*
# Frontend JS | Vendor: FairVerify | App: Phoenix | Type: VisitorLeadConfig
# FairVerify Event Solutions (fairverify.de) based on GodMode Core - GodMode.Systems
# Copyright Tim Haymann, FairVerify Event Solutions GmbH.
# created 2026-03-13 by Codex
# last edited 2026-03-15 by Codex // return validation error codes instead of localized messages
*/
export const VISITOR_LEAD_TICKET_PREFIX = 'FV'
export const VISITOR_LEAD_EXHIBITOR_PREFIX = 'FVL'
export const VISITOR_LEAD_SCAN_HOST = 'FAIRVERIFY.APP'
export const VISITOR_LEAD_SCAN_PATH = 'LEADSCAN'
export const ALLOW_VISITOR_LEAD_DEBUG = process.env.NODE_ENV !== 'production'
export const VISITOR_LEAD_DEBUG_PASSWORD = process.env.VUE_APP_VISITOR_LEAD_DEBUG_PASSWORD || ''
export const VISITOR_LEAD_DEBUG_TAP_COUNT = 5
export const VISITOR_LEAD_DEBUG_TAP_WINDOW_MS = 2500

const VISITOR_LEAD_TICKET_PATTERN = /^FV(?!L)([^.\s]+)\.([^\s]+)$/
const VISITOR_LEAD_EXHIBITOR_PATTERN = /^FVL([^.\s]+)\.([^\s]+)$/
const VISITOR_LEAD_URL_PATTERN = /^HTTPS?:\/\/([^/\s?#]+)\/([^/\s?#]+)\/([^/\s?#]+)\/([^/\s?#]+)\/([^?\s#]+)$/

function normalizeLeadInput(value) {
  return String(value || '').trim().toUpperCase()
}

function parseLeadPayload(payload) {
  const normalizedPayload = normalizeLeadInput(payload)
  if (!normalizedPayload) return null

  const exhibitorMatch = VISITOR_LEAD_EXHIBITOR_PATTERN.exec(normalizedPayload)
  if (exhibitorMatch) {
    return {
      normalizedCode: normalizedPayload,
      payload: normalizedPayload,
      fairId: exhibitorMatch[1],
      identifier: exhibitorMatch[2],
      codeType: 'exhibitor',
      source: 'raw'
    }
  }

  const ticketMatch = VISITOR_LEAD_TICKET_PATTERN.exec(normalizedPayload)
  if (ticketMatch) {
    return {
      normalizedCode: normalizedPayload,
      payload: normalizedPayload,
      fairId: ticketMatch[1],
      identifier: ticketMatch[2],
      codeType: 'ticket',
      source: 'raw'
    }
  }

  return null
}

export function parseVisitorLeadCode(inputValue) {
  const normalizedInput = normalizeLeadInput(inputValue)
  if (!normalizedInput) return null

  const urlMatch = VISITOR_LEAD_URL_PATTERN.exec(normalizedInput)
  if (urlMatch) {
    const [, host, pathSegment, fairIdFromUrl, language, payload] = urlMatch
    if (host !== VISITOR_LEAD_SCAN_HOST || pathSegment !== VISITOR_LEAD_SCAN_PATH) {
      return null
    }

    const parsedPayload = parseLeadPayload(payload)
    if (!parsedPayload || parsedPayload.fairId !== fairIdFromUrl) {
      return null
    }

    return {
      ...parsedPayload,
      normalizedInput,
      source: 'url',
      language
    }
  }

  return parseLeadPayload(normalizedInput)
}

export function getVisitorLeadValidationError(inputValue) {
  const normalizedInput = normalizeLeadInput(inputValue)
  if (!normalizedInput) {
    return 'empty_code'
  }

  if (normalizedInput.startsWith('HTTP')) {
    const urlMatch = VISITOR_LEAD_URL_PATTERN.exec(normalizedInput)
    if (!urlMatch) {
      return 'invalid_leadscan_url_format'
    }

    const [, host, pathSegment, fairIdFromUrl, , payload] = urlMatch
    if (host !== VISITOR_LEAD_SCAN_HOST || pathSegment !== VISITOR_LEAD_SCAN_PATH) {
      return 'invalid_leadscan_host'
    }

    const parsedPayload = parseLeadPayload(payload)
    if (!parsedPayload) {
      return 'invalid_leadscan_payload_format'
    }

    if (parsedPayload.fairId !== fairIdFromUrl) {
      return 'inconsistent_fair_id'
    }

    return null
  }

  if (!normalizedInput.startsWith(VISITOR_LEAD_TICKET_PREFIX)) {
    return 'invalid_code_prefix'
  }

  if (!parseLeadPayload(normalizedInput)) {
    return 'invalid_leadscan_payload_format'
  }

  return null
}
